import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { FormProvider, useForm } from "react-hook-form";
import { DISCOUNT_TYPES, DISCOUNT_TYPES_OPTIONS } from "consts";

import {
  PaziaFormButton,
  PaziaSelect,
  PaziaInput,
  PaziaTextarea,
  IDropdownOption,
  PaziaDateSelect,
  PaziaButton,
  PaziaMultiSelect,
  PaziaCheckbox,
} from "components/atoms";
import { PaziaPrefixInput } from "components/atoms/Inputs";
import { useCourseGroupsOptions, useCoursesOptions } from "api/hooks";
import { DiscountCodeApi } from "api/payment-discounts";
import { useCategoriesOptions } from "api/categories";
import { useUsersOptions } from "api/hooks/useUsers";
import { PaziaBox, PaziaTabs } from "components/atoms";
import { DISCOUNT_MODE_OPTIONS, BUNDLE_REQUIREMENT_MODE_OPTIONS } from "../consts";

interface DiscountFormProps {
  onSubmit: (data: DiscountFormValues) => Promise<void>;
  code?: DiscountCodeApi;
  resourceTypeOptions: IDropdownOption[];
  onCancel?: () => void;
}
const bundleRequirementModeFromApi = (
  value: number | string | null | undefined,
): "courseList" | "courseGroupList" | "categoryMinCount" => {
  if (value === 1 || value === "courseGroupList") return "courseGroupList";
  if (value === 2 || value === "categoryMinCount") return "categoryMinCount";
  return "courseList";
};

export const DiscountForm = ({ onSubmit, code, resourceTypeOptions, onCancel }: DiscountFormProps) => {
  const { isLoading: loadingCourses, options: coursesOptions } = useCoursesOptions();
  const { loading: loadingGroups, options: groupsOptioons } = useCourseGroupsOptions();
  const { isLoading: loadingUsers, options: usersOptions } = useUsersOptions();
  const { loading: loadingCategories, options: categoriesOptions } = useCategoriesOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      code: code?.code,
      description: code?.description,
      from: code?.from,
      to: code?.to,
      numberOfUses: code?.numberOfUses,
      type: code?.type,
      value: code?.value,
      // Mode selector
      mode: code?.mode || "standard",
      // Standard mode fields
      resourceType: code?.user
        ? "user"
        : code?.course
          ? "course"
          : code?.courseGroup
            ? "course-group"
            : code?.categories?.length
              ? "category"
              : "general",
      resourceId: code?.user?.id || code?.course?.id || code?.courseGroup?.id,
      categoryIds: code?.categories?.map((c) => c.id) || [],
      // Bundle mode fields
      bundleRequirementMode: bundleRequirementModeFromApi(code?.bundleRequirementMode),
      requiredCourseIds: code?.requiredCourses?.map((c) => c.id) || [],
      benefitCourseIds: code?.benefitCourses?.map((c) => c.id) || [],
      requiredCourseGroupIds: code?.requiredCourseGroups?.map((g) => g.id) || [],
      benefitCourseGroupIds: code?.benefitCourseGroups?.map((g) => g.id) || [],
      bundleCategoryId: code?.bundleCategoryId || undefined,
      bundleMinCount: code?.bundleMinCount || undefined,
      autoApply: code?.autoApply || false,
    },
  });

  const { handleSubmit, watch, setValue } = methods;
  const onSubmitHandler = async (data: DiscountFormValues) => {
    await onSubmit(data);
  };

  const mode = watch(DISCOUNT_FORM_NAMES.MODE);
  const bundleRequirementMode = watch(DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE);
  const resourceType = watch(DISCOUNT_FORM_NAMES.RESOURCE_TYPE);
  const type = watch(DISCOUNT_FORM_NAMES.TYPE);

  const range = useMemo(() => {
    const start = new Date(2020, 0, 1);
    const fourYearsAhead = new Date();
    fourYearsAhead.setFullYear(fourYearsAhead.getFullYear() + 4);
    return [start, fourYearsAhead] as [Date, Date];
  }, []);

  const modeTabs = useMemo(
    () =>
      DISCOUNT_MODE_OPTIONS.map((option) => ({
        name: option.label,
        active: mode === option.value,
        disabled: !!code?.id, // Disable tab switch when editing existing code
        onClick: () => {
          if (!!code?.id) return;
          setValue(DISCOUNT_FORM_NAMES.MODE, option.value);
          // Reset mode-specific fields when mode changes
          setValue(DISCOUNT_FORM_NAMES.RESOURCE_TYPE, "general");
          setValue(DISCOUNT_FORM_NAMES.RESOURCE_ID, 0);
          setValue(DISCOUNT_FORM_NAMES.CATEGORY_IDS, []);
          setValue(DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE, "courseList");
          setValue(DISCOUNT_FORM_NAMES.REQUIRED_COURSE_IDS, []);
          setValue(DISCOUNT_FORM_NAMES.BENEFIT_COURSE_IDS, []);
          setValue(DISCOUNT_FORM_NAMES.REQUIRED_COURSE_GROUP_IDS, []);
          setValue(DISCOUNT_FORM_NAMES.BENEFIT_COURSE_GROUP_IDS, []);
          setValue(DISCOUNT_FORM_NAMES.BUNDLE_CATEGORY_ID, undefined);
          setValue(DISCOUNT_FORM_NAMES.BUNDLE_MIN_COUNT, undefined);
          setValue(DISCOUNT_FORM_NAMES.AUTO_APPLY, false);
        },
      })),
    [mode, code?.id, setValue],
  );

  const getModeDescription = () => {
    switch (mode) {
      case "standard":
        return {
          title: "Standardowy kod rabatowy",
          description:
            "Tradycyjny kod zniżkowy, który studenci wpisują podczas płatności. Możesz przypisać go do konkretnego kursu, grupy kursów, użytkownika lub uczynić ogólnym.",
          icon: "🎫",
        };
      case "bundle":
        return {
          title: "Rabat na pakiet kursów",
          description:
            "Zniżka aktywowana automatycznie, gdy student kupuje określone kursy razem. Wybierz kursy wymagane oraz te, na które ma zostać udzielona zniżka.",
          icon: "📦",
        };
      default:
        return { title: "", description: "", icon: "" };
    }
  };

  const modeInfo = getModeDescription();

  return (
    <PaziaBox className="">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-8">
          {/* Mode Selection */}
          <div>
            <h2 className="mb-4 text-xl font-bold text-slate-800 dark:text-slate-100">Wybierz typ rabatu</h2>
            <PaziaTabs tabs={modeTabs} />

            {/* Mode Description */}
            <div className="mt-6 rounded-lg bg-slate-50 p-6 dark:bg-slate-800/50">
              <div className="flex items-start gap-4">
                <div className="text-4xl">{modeInfo.icon}</div>
                <div className="flex-1">
                  <h3 className="mb-2 text-lg font-semibold text-slate-800 dark:text-slate-100">{modeInfo.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{modeInfo.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">Podstawowe informacje</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <PaziaInput
                name={DISCOUNT_FORM_NAMES.CODE}
                label="Kod rabatowy"
                required={true}
                disabled={!!code?.id}
                placeholder="np. LATO2024"
              />
              <PaziaTextarea
                name={DISCOUNT_FORM_NAMES.DESCRIPTION}
                label="Opis wewnętrzny"
                required
                placeholder="Krótki opis do czego służy ten rabat"
              />
            </div>
          </div>

          {/* Mode-Specific Configuration */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">Konfiguracja rabatu</h3>
            <div className="space-y-4">
              {mode === "standard" && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                    <h4 className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">Zakres zastosowania</h4>
                    <div className="grid gap-4 md:grid-cols-2">
                      <PaziaSelect
                        name={DISCOUNT_FORM_NAMES.RESOURCE_TYPE}
                        label="Powiązanie"
                        onChange={() => setValue(DISCOUNT_FORM_NAMES.RESOURCE_ID, 0)}
                        required
                        disabled={resourceTypeOptions.length === 1 || resourceType === "user" || !!code?.id}
                        options={
                          resourceType !== "user"
                            ? resourceTypeOptions.filter((x) => x.value !== "user")
                            : resourceTypeOptions
                        }
                      />
                      {resourceType === "course" && (
                        <PaziaSelect
                          name={DISCOUNT_FORM_NAMES.RESOURCE_ID}
                          disabled={!!code?.id}
                          label="Wybierz kurs"
                          required
                          options={coursesOptions}
                          isLoading={loadingCourses}
                        />
                      )}
                      {resourceType === "user" && (
                        <PaziaSelect
                          name={DISCOUNT_FORM_NAMES.RESOURCE_ID}
                          label="Użytkownik"
                          disabled
                          required
                          options={usersOptions}
                          isLoading={loadingUsers}
                        />
                      )}
                      {resourceType === "course-group" && (
                        <PaziaSelect
                          name={DISCOUNT_FORM_NAMES.RESOURCE_ID}
                          disabled={!!code?.id}
                          label="Wybierz grupę kursów"
                          required
                          options={groupsOptioons}
                          isLoading={loadingGroups}
                        />
                      )}
                      {resourceType === "category" && (
                        <PaziaMultiSelect
                          name={DISCOUNT_FORM_NAMES.CATEGORY_IDS}
                          disabled={!!code?.id}
                          label="Wybierz kategorie"
                          options={categoriesOptions}
                          isLoading={loadingCategories}
                          required
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {mode === "bundle" && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                    <h4 className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                      Co spełnia warunek aktywacji rabatu?
                    </h4>
                    <PaziaSelect
                      name={DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE}
                      label="Typ wymagań"
                      options={BUNDLE_REQUIREMENT_MODE_OPTIONS}
                      required
                      disabled={!!code?.id}
                    />
                  </div>

                  {bundleRequirementMode === "courseList" && (
                    <>
                      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                        <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                          1. Wymagane kursy
                        </h4>
                        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                          Student musi mieć w koszyku wszystkie te konkretne edycje kursów.
                        </p>
                        <PaziaMultiSelect
                          name={DISCOUNT_FORM_NAMES.REQUIRED_COURSE_IDS}
                          label="Wymagane kursy"
                          options={coursesOptions}
                          isLoading={loadingCourses}
                          required
                          disabled={!!code?.id}
                        />
                      </div>

                      <div className="flex items-center justify-center text-2xl text-slate-400">↓</div>

                      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
                        <h4 className="mb-2 text-sm font-medium text-green-700 dark:text-green-300">
                          2. Kursy ze zniżką
                        </h4>
                        <p className="mb-3 text-xs text-green-600 dark:text-green-400">
                          Na te kursy zostanie zastosowany rabat.
                        </p>
                        <PaziaMultiSelect
                          name={DISCOUNT_FORM_NAMES.BENEFIT_COURSE_IDS}
                          label="Kursy ze zniżką"
                          options={coursesOptions}
                          isLoading={loadingCourses}
                          required
                          disabled={!!code?.id}
                        />
                      </div>
                    </>
                  )}

                  {bundleRequirementMode === "courseGroupList" && (
                    <>
                      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                        <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                          1. Wymagane grupy kursów
                        </h4>
                        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                          Wystarczy dowolna edycja z każdej wskazanej grupy w koszyku.
                        </p>
                        <PaziaMultiSelect
                          name={DISCOUNT_FORM_NAMES.REQUIRED_COURSE_GROUP_IDS}
                          label="Wymagane grupy"
                          options={groupsOptioons}
                          isLoading={loadingGroups}
                          required
                          disabled={!!code?.id}
                        />
                      </div>

                      <div className="flex items-center justify-center text-2xl text-slate-400">↓</div>

                      <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950/30">
                        <h4 className="mb-2 text-sm font-medium text-green-700 dark:text-green-300">
                          2. Grupy ze zniżką
                        </h4>
                        <p className="mb-3 text-xs text-green-600 dark:text-green-400">
                          Rabat zostanie rozdzielony proporcjonalnie na kursy z koszyka należące do tych grup.
                        </p>
                        <PaziaMultiSelect
                          name={DISCOUNT_FORM_NAMES.BENEFIT_COURSE_GROUP_IDS}
                          label="Grupy ze zniżką"
                          options={groupsOptioons}
                          isLoading={loadingGroups}
                          required
                          disabled={!!code?.id}
                        />
                      </div>
                    </>
                  )}

                  {bundleRequirementMode === "categoryMinCount" && (
                    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                      <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                        Próg: min N kursów z kategorii
                      </h4>
                      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                        Rabat aktywuje się gdy w koszyku jest co najmniej N kursów z wybranej kategorii.
                        Aplikowany jest tylko na kursy z tej kategorii.
                      </p>
                      <div className="grid gap-4 md:grid-cols-2">
                        <PaziaSelect
                          name={DISCOUNT_FORM_NAMES.BUNDLE_CATEGORY_ID}
                          label="Kategoria"
                          required
                          options={categoriesOptions}
                          isLoading={loadingCategories}
                          disabled={!!code?.id}
                        />
                        <PaziaInput
                          name={DISCOUNT_FORM_NAMES.BUNDLE_MIN_COUNT}
                          label="Minimalna liczba kursów"
                          type="number"
                          required
                          disabled={!!code?.id}
                        />
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                    <PaziaCheckbox
                      name={DISCOUNT_FORM_NAMES.AUTO_APPLY}
                      label="Aktywuj automatycznie (bez wpisywania kodu przez klienta)"
                    />
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Jeśli zaznaczone, rabat aplikuje się sam gdy spełnione są wymagania.
                      Klient nie musi znać kodu (kod nadal istnieje dla referencji administracyjnej).
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Discount Value */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">Wartość rabatu</h3>
            <div className="grid gap-4 md:grid-cols-3">
              <PaziaSelect
                name={DISCOUNT_FORM_NAMES.TYPE}
                label="Typ rabatu"
                options={DISCOUNT_TYPES_OPTIONS}
                required
                disabled={!!code?.id}
              />
              <PaziaPrefixInput
                name={DISCOUNT_FORM_NAMES.VALUE}
                required
                label="Wartość"
                disabled={!!code?.id}
                type="number"
                prefix={type === DISCOUNT_TYPES.PERCENTAGE ? "%" : "PLN"}
              />
              <PaziaInput
                name={DISCOUNT_FORM_NAMES.NUMBER_OF_USES}
                placeholder="Bez limitu"
                label="Limit użyć"
                type="number"
              />
            </div>
          </div>

          {/* Validity Period */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">Okres ważności</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <PaziaDateSelect name={DISCOUNT_FORM_NAMES.FROM} label="Data rozpoczęcia" range={range} />
              <PaziaDateSelect name={DISCOUNT_FORM_NAMES.TO} label="Data zakończenia" range={range} />
            </div>
          </div>

          {!!code?.id && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50 text-sm">
              Aby tymczasowo wyłączyć ten rabat, użyj przełącznika w kolumnie „Aktywny" na liście kodów rabatowych.
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-slate-200 pt-6 dark:border-slate-700">
            {onCancel && <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCancel} />}
            <PaziaFormButton title="Zapisz" type="success" />
          </div>
        </form>
      </FormProvider>
    </PaziaBox>
  );
};

const DISCOUNT_FORM_NAMES = {
  CODE: "code",
  DESCRIPTION: "description",
  FROM: "from",
  TO: "to",
  NUMBER_OF_USES: "numberOfUses",
  TYPE: "type",
  VALUE: "value",
  // Mode selector
  MODE: "mode",
  // Standard mode fields
  RESOURCE_TYPE: "resourceType",
  RESOURCE_ID: "resourceId",
  CATEGORY_IDS: "categoryIds",
  // Bundle mode fields
  BUNDLE_REQUIREMENT_MODE: "bundleRequirementMode",
  REQUIRED_COURSE_IDS: "requiredCourseIds",
  BENEFIT_COURSE_IDS: "benefitCourseIds",
  REQUIRED_COURSE_GROUP_IDS: "requiredCourseGroupIds",
  BENEFIT_COURSE_GROUP_IDS: "benefitCourseGroupIds",
  BUNDLE_CATEGORY_ID: "bundleCategoryId",
  BUNDLE_MIN_COUNT: "bundleMinCount",
  AUTO_APPLY: "autoApply",
} as const;
const schema = yup
  .object({
    // Common fields
    [DISCOUNT_FORM_NAMES.DESCRIPTION]: yup.string().required(),
    [DISCOUNT_FORM_NAMES.FROM]: yup.date().nullable().optional(),
    [DISCOUNT_FORM_NAMES.TO]: yup
      .date()
      .nullable()
      .optional()
      .when(DISCOUNT_FORM_NAMES.FROM, {
        is: (from: Date) => from != null,
        then: (schema) =>
          schema.min(yup.ref(DISCOUNT_FORM_NAMES.FROM), "Data zakończenia musi być późniejsza niż data rozpoczęcia"),
      }),
    [DISCOUNT_FORM_NAMES.NUMBER_OF_USES]: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable(),
    [DISCOUNT_FORM_NAMES.TYPE]: yup.number().required(),
    [DISCOUNT_FORM_NAMES.VALUE]: yup.number().required(),
    [DISCOUNT_FORM_NAMES.MODE]: yup.string().required(),

    // Code field - required for all modes
    [DISCOUNT_FORM_NAMES.CODE]: yup.string().required(),

    // Standard mode fields
    [DISCOUNT_FORM_NAMES.RESOURCE_TYPE]: yup.string().when(DISCOUNT_FORM_NAMES.MODE, {
      is: "standard",
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    [DISCOUNT_FORM_NAMES.RESOURCE_ID]: yup
      .number()
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.RESOURCE_TYPE], {
        is: (mode: string, resourceType: string) =>
          mode === "standard" && resourceType !== "general" && resourceType !== "category",
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.CATEGORY_IDS]: yup
      .array()
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.RESOURCE_TYPE], {
        is: (mode: string, resourceType: string) => mode === "standard" && resourceType === "category",
        then: (schema) => schema.of(yup.number()).min(1, "Wybierz przynajmniej jedną kategorię").required(),
        otherwise: (schema) => schema.optional(),
      }),

    // Bundle mode fields
    [DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE]: yup.string().when(DISCOUNT_FORM_NAMES.MODE, {
      is: "bundle",
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),

    [DISCOUNT_FORM_NAMES.REQUIRED_COURSE_IDS]: yup
      .array()
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE], {
        is: (m: string, brm: string) => m === "bundle" && brm === "courseList",
        then: (schema) => schema.of(yup.number()).min(1, "Wybierz przynajmniej jeden kurs").required(),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.BENEFIT_COURSE_IDS]: yup
      .array()
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE], {
        is: (m: string, brm: string) => m === "bundle" && brm === "courseList",
        then: (schema) => schema.of(yup.number()).min(1, "Wybierz przynajmniej jeden kurs").required(),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.REQUIRED_COURSE_GROUP_IDS]: yup
      .array()
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE], {
        is: (m: string, brm: string) => m === "bundle" && brm === "courseGroupList",
        then: (schema) => schema.of(yup.number()).min(1, "Wybierz przynajmniej jedną grupę").required(),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.BENEFIT_COURSE_GROUP_IDS]: yup
      .array()
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE], {
        is: (m: string, brm: string) => m === "bundle" && brm === "courseGroupList",
        then: (schema) => schema.of(yup.number()).min(1, "Wybierz przynajmniej jedną grupę").required(),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.BUNDLE_CATEGORY_ID]: yup
      .number()
      .transform((v) => (isNaN(v) ? undefined : v))
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE], {
        is: (m: string, brm: string) => m === "bundle" && brm === "categoryMinCount",
        then: (schema) => schema.required("Wybierz kategorię"),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.BUNDLE_MIN_COUNT]: yup
      .number()
      .transform((v) => (isNaN(v) ? undefined : v))
      .when([DISCOUNT_FORM_NAMES.MODE, DISCOUNT_FORM_NAMES.BUNDLE_REQUIREMENT_MODE], {
        is: (m: string, brm: string) => m === "bundle" && brm === "categoryMinCount",
        then: (schema) => schema.min(1, "Min. 1").required(),
        otherwise: (schema) => schema.optional(),
      }),

    [DISCOUNT_FORM_NAMES.AUTO_APPLY]: yup.boolean().optional(),
  })
  .required();
export type DiscountFormValues = yup.InferType<typeof schema>;
